import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../lib/api';

const ACCENT = '#f97316';

const ACTION_COLORS: Record<string, string> = {
  created: '#10b981',
  updated: '#3b82f6',
  deleted: '#ef4444',
  resolved: '#8b5cf6',
  started: '#f59e0b',
  completed: '#10b981',
  failed: '#ef4444',
  paused: '#6b7280',
};

interface AuditEntry {
  id: string;
  org_id: string;
  event_type: string;
  actor_id: string;
  actor_type: 'user' | 'system' | 'webhook';
  resource_type: string;
  resource_id: string;
  action: string;
  details: Record<string, unknown>;
  ip_address: string | null;
  created_at: string;
}

function Badge({ label, color }: { label: string; color: string }) {
  return (
    <span style={{
      display: 'inline-block',
      padding: '2px 8px',
      borderRadius: 12,
      fontSize: '0.75rem',
      fontWeight: 600,
      backgroundColor: `${color}18`,
      color,
      textTransform: 'capitalize',
    }}>
      {label.replace(/_/g, ' ')}
    </span>
  );
}

export default function AuditPage() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  // Filters
  const [eventType, setEventType] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const buildQuery = useCallback(() => {
    const parts: string[] = [];
    if (eventType) parts.push(`event_type=${encodeURIComponent(eventType)}`);
    if (resourceType) parts.push(`resource_type=${encodeURIComponent(resourceType)}`);
    if (from) parts.push(`from=${encodeURIComponent(from)}`);
    if (to) parts.push(`to=${encodeURIComponent(to)}`);
    return parts.length > 0 ? `?${parts.join('&')}` : '';
  }, [eventType, resourceType, from, to]);

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get<{ data: AuditEntry[] }>(`/audit${buildQuery()}`);
      setEntries(res.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [buildQuery]);

  useEffect(() => { fetchEntries(); }, [fetchEntries]);

  const eventTypes = [...new Set(entries.map(e => e.event_type))];
  const resourceTypes = [...new Set(entries.map(e => e.resource_type))];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 700, margin: 0, color: '#111827' }}>Audit Log</h1>
          <p style={{ margin: '0.25rem 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Track all actions and changes across Bridge integrations
          </p>
        </div>
      </div>

      {/* Filter row */}
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={eventType}
          onChange={e => setEventType(e.target.value)}
          style={selectStyle}
        >
          <option value="">All Event Types</option>
          {eventTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <select
          value={resourceType}
          onChange={e => setResourceType(e.target.value)}
          style={selectStyle}
        >
          <option value="">All Resource Types</option>
          {resourceTypes.map(t => <option key={t} value={t}>{t}</option>)}
        </select>

        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: '0.8rem', color: '#6b7280' }}>From:</label>
          <input
            type="date"
            value={from}
            onChange={e => setFrom(e.target.value)}
            style={inputStyle}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
          <label style={{ fontSize: '0.8rem', color: '#6b7280' }}>To:</label>
          <input
            type="date"
            value={to}
            onChange={e => setTo(e.target.value)}
            style={inputStyle}
          />
        </div>
      </div>

      {/* Entries table */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={thStyle}>Timestamp</th>
              <th style={thStyle}>Event</th>
              <th style={thStyle}>Resource</th>
              <th style={thStyle}>Action</th>
              <th style={thStyle}>Actor</th>
              <th style={thStyle}>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>Loading...</td></tr>
            ) : entries.length === 0 ? (
              <tr><td colSpan={6} style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>No audit entries found</td></tr>
            ) : entries.map(entry => (
              <React.Fragment key={entry.id}>
                <tr
                  style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onClick={() => setExpandedId(expandedId === entry.id ? null : entry.id)}
                >
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {new Date(entry.created_at).toLocaleString()}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{entry.event_type}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.8rem' }}>{entry.resource_type}</span>
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontFamily: 'monospace' }}>{entry.resource_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <Badge label={entry.action} color={ACTION_COLORS[entry.action] || '#6b7280'} />
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.8rem' }}>{entry.actor_type}</span>
                    <br />
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af', fontFamily: 'monospace' }}>{entry.actor_id}</span>
                  </td>
                  <td style={tdStyle}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                      {expandedId === entry.id ? '\u25B2' : '\u25BC'} {Object.keys(entry.details).length} fields
                    </span>
                  </td>
                </tr>
                {expandedId === entry.id && (
                  <tr>
                    <td colSpan={6} style={{ padding: '0.75rem 1rem', backgroundColor: '#f9fafb' }}>
                      <pre style={{
                        margin: 0,
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        color: '#374151',
                      }}>
                        {JSON.stringify(entry.details, null, 2)}
                      </pre>
                      {entry.ip_address && (
                        <div style={{ marginTop: '0.5rem', fontSize: '0.75rem', color: '#9ca3af' }}>
                          IP: {entry.ip_address}
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const selectStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
  backgroundColor: '#fff',
};

const inputStyle: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid #d1d5db',
  fontSize: '0.875rem',
};

const thStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  textAlign: 'left',
  fontWeight: 600,
  color: '#374151',
  fontSize: '0.8rem',
};

const tdStyle: React.CSSProperties = {
  padding: '0.625rem 0.75rem',
  color: '#111827',
};
